import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FleetChargingFigureService } from 'src/app/_services/fleet-charging-figure.service';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components'; 
import { VehicleChargingFigureService } from 'src/app/_services/vehicle-charging-figure.service';
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-soc-delta-per-charging-ef-chart',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './soc-delta-per-charging-ef-chart.component.html',
  styleUrls: ['./soc-delta-per-charging-ef-chart.component.scss']
})
export class SocDeltaPerChargingEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() fleetId: string[] = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private colorMap: {[key:string]:string}={};
      chartRange!: { range: [number, number] };

  constructor(
    private readonly fleetChargingFigureService: FleetChargingFigureService,
    private readonly vehicleChargingFigureService: VehicleChargingFigureService
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetChargingFigureService.getSocDeltaFleet(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const fleetData = this.plotFleetSocDeltaPerCharging(datapoints);
          this.fetchVehicleInfoAndUpdateChart(fleetData);
        },
        error: (error) => {
          console.error('Error fetching State of Charge Difference Fleet data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        }
      }
    )
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });
    this.initializeColorMap();

    this.vehicleChargingFigureService.getSocDelta(deviceSerialNumber).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (data: any) => {
        const plotData = Object.keys(data).flatMap((deviceSerialNumberId: string) => {
          const deviceDataArray = data[deviceSerialNumberId];
          const vehicle = this.selectedVehicles.find(vehicle => vehicle.batteryIdentifier === deviceSerialNumberId);
          const bin = deviceDataArray.map((dataObject: any) => dataObject.binSocDifference);
          const soc = deviceDataArray.map((dataObject: any) => dataObject.chargingSessions);
      
          return {
            x: bin,
            y: soc,
            type: 'bar',
            name: vehicle.name,
            marker: {
              color: this.colorMap[deviceSerialNumberId]
            }
          };
        });

        const combinedPlotData = [...fleetData, ...plotData];
        this.updateChart(combinedPlotData);
        this.dataLoading = false;
      },
      error: (error) => {
        console.error('Error fetching State of Charge Difference data:', error);
        this.dataLoading = false;
        this.errorMessage = true;
      }
    });
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotFleetSocDeltaPerCharging(datapoints: any): any [] {
    const transformXAxisPipe = new TransformXAxisPipe();
    return Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];

      const socDelta = plotData.map((dataObject: any) => dataObject.binSocDifference);
      const chargingEvents = plotData.map((dataObject: any) => dataObject.chargingSessions);
      this.chartRange = transformXAxisPipe.transform(socDelta, chargingEvents, 5,100);
      return {
        x: socDelta,
        y: chargingEvents,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097' 
        }
      };
    });
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'State of Charge Difference per Charging Session',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'State of Charge Increase [%]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Percentage of Charging Sessions [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('soc-delta-per-charging-procedure-ef-chart-chart', data, layout);
  }

  initializeColorMap(): void {
    const colors = ['#007bc0', '#18837e','#9e2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

}

